import { useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { Add, ClearAll, Edit, Merge, Remove } from "@mui/icons-material";
import { PageTitle } from "../../layout/PageTitle";
import { createListStore, ListProp } from "../../common/listStore";
import { listHeaderItems, renderListStore } from "../../common/listStoreUi";
import { useDialog, useSimpleDialog, yesNoButtons } from "../../common/dialog";
import { RejectedImportCustomerDialog, RejectedImportCustomerDto, RejectedImportCustomerDtoBase, RejectedImportItemDto } from "./RejectedImportCustomerDialog";
import { useCustomerSexTypes, useCustomerStates, useCustomerFlagsEnum } from "../../common/enum";
import { map } from "rxjs";
import { createAxios } from "../../common/axios";

const customerColumns: ListProp<RejectedImportCustomerDtoBase>[] = [
    { column: "title", label: "Title", width: 120 },
    { column: "fname", label: "First name", width: 120 },
    { column: "lname", label: "Last name", width: 150 },
    { column: "strt1", label: "Street", width: 220 },
    { column: "strt2", label: "House nr.", width: 100 },
    { column: "coadr", label: "C/O Address", width: 220 },
    { column: "zipc", label: "ZIP code", width: 100 },
    { column: "city", label: "City", width: 200 },
    { column: "tel", label: "Tel", width: 100 },
    { column: "mob", label: "Mobile", width: 100 },
    { column: "email", label: "Email", width: 250 },
    { column: "sexTypeId", label: "Gender", width: 100, enumStore: useCustomerSexTypes },
    { column: "mailStopId", label: "Mail stop", width: 100, enumStore: useCustomerFlagsEnum },
    { column: "emailStopId", label: "E-mail stop", width: 100, enumStore: useCustomerFlagsEnum },
    { column: "phoneStopId", label: "Phone stop", width: 100, enumStore: useCustomerFlagsEnum },
    { column: "sc1", label: "SC1", width: 100 },
    { column: "sc2", label: "SC2", width: 100 },
];

export function RejectedImportItemListPage() {
    const [{ useItemsStore, useCustomersStore }] = useState(() => {
        const useItemsStore = createListStore<RejectedImportItemDto>({
            path: "reports/rejectedImportItems",
            autoSelectFirstItem: true,
            listProps: [
                { column: "rejectDate", label: "Rej. date", width: 120, type: "date" },
                { column: "rejectReason", label: "Reject reason", width: 160 },
                { column: "custId", label: "Customer ID", width: 120 },
                ...customerColumns,
            ], itemsPerPage: 10
        });
        const useCustomersStore = createListStore<RejectedImportCustomerDto>({
            data: useItemsStore.getState().selectedItem$.pipe(map(p => ({
                items: p == null ? [] : p.customers, isFetching: false
            }))),
            autoSelectFirstItem: true,
            listProps: [
                { column: "customerStateId", label: "Status", width: 120, enumStore: useCustomerStates },
                { column: "custId", label: "Customer ID", width: 120 },
                ...customerColumns,
                { column: "mailStatusId", label: "Mail status", width: 100, enumStore: useCustomerFlagsEnum },
                { column: "emailStatusId", label: "Email status", width: 100, enumStore: useCustomerFlagsEnum },
                { column: "emailStopSystemId", label: "Email stop system", width: 100, enumStore: useCustomerFlagsEnum },
                { column: "telStatusId", label: "Phone status", width: 100, enumStore: useCustomerFlagsEnum },
                { column: "mobStatusId", label: "Mobile status", width: 100, enumStore: useCustomerFlagsEnum },
                { column: "loserStatusId", label: "Loser status", width: 100, enumStore: useCustomerFlagsEnum },
            ], itemsPerPage: 3
        });
        return { useItemsStore, useCustomersStore };
    });

    const { selectedItem, isFetching, hasResolvedItems } = useItemsStore(p => ({ selectedItem: p.selectedItem, isFetching: p.fetching, hasResolvedItems: p.items.some(p => p.isResolved) }));
    const { selectedCustomer } = useCustomersStore(p => ({ selectedCustomer: p.selectedItem }));
    const [isQueryingServer, setIsQueryingServer] = useState(false);

    const simpleDialog = useSimpleDialog();

    async function add() {
        try {
            setIsQueryingServer(true);
            const { data } = await createAxios().post("reports/rejectedImportItems/processNewCustomer", { itemId: selectedItem!.id });
            simpleDialog.show("Result", data.message);
        } finally {
            setIsQueryingServer(false);
            refetch();
        }
    }

    function refetch() {
        useItemsStore.getState().fetch();
    }

    async function removeRejectedItem() {

        await simpleDialog.show("Delete rejected item", "Do you really want to delete rejected item?", undefined, yesNoButtons(async () => {
            try {
                setIsQueryingServer(true);
                await createAxios().post("reports/rejectedImportItems/removeRejectedItem", { itemId: selectedItem!.id });
            } finally {
                setIsQueryingServer(false);
                refetch();
            }
        }));
    }

    async function removeResolvedItems() {
        try {
            setIsQueryingServer(true);
            await createAxios().post("reports/rejectedImportItems/removeResolved");
        } finally {
            setIsQueryingServer(false);
            refetch();
        }
    }

    const mergeDialog = useDialog(close => <RejectedImportCustomerDialog close={close} item={selectedItem!} customer={selectedCustomer!}
        onSaved={message => {
            simpleDialog.show("Result", message);
            refetch();
        }} />);

    return <>
        {mergeDialog.render()}
        {simpleDialog.render()}
        <Box>
            <PageTitle title="Rejected duplicates" />
            {renderListStore(useItemsStore, {
                onDoubleClick: p => {
                    if (p.isResolved) {
                        window.open(`/customers/${p.custId}`, "_blank");
                    }
                },
                header: listHeaderItems([
                    ["remove", () => <Button disabled={isQueryingServer || isFetching || selectedItem == null}
                        title="Remove selected" style={{ marginRight: 10 }} onClick={ removeRejectedItem } color="negative" > <Remove /></Button>],
                    ["edit", () => <Button color="edit" disabled={selectedItem == null || !selectedItem.isResolved || isQueryingServer || isFetching}
                        title="Open existing customer in new window" onClick={() => window.open(`/customers/${selectedItem!.custId}`, "_blank")}><Edit /></Button>],
                    ["clear", () => <Button disabled={isQueryingServer || isFetching || !hasResolvedItems}
                        title="Remove all resolved duplicates" onClick={removeResolvedItems}><ClearAll /></Button>],
                ])
            })}
        </Box>
        <Box>
            <Typography variant="h6" mt={2}>Existing customers</Typography>
            {renderListStore(useCustomersStore, {
                onDoubleClick: p => {
                    window.open(`/customers/${p.custId}`, "_blank");
                },
                header: listHeaderItems([
                    ["edit", () => <Button color="edit" disabled={selectedCustomer == null || isQueryingServer || isFetching}
                        title="Open existing customer in new window" onClick={() => window.open(`/customers/${selectedCustomer!.custId}`, "_blank")}><Edit /></Button>],
                    ["add", () => <Button color="add" disabled={selectedCustomer == null || isQueryingServer || isFetching || selectedItem == null || selectedItem.isResolved}
                        title="Add as new customer" onClick={add}><Add /></Button>],
                    ["merge", () => <Button disabled={selectedCustomer == null || isQueryingServer || isFetching || selectedItem == null || selectedItem.isResolved}
                        title="Attach to existing customer" onClick={() => mergeDialog.show()}><Merge /></Button>],
                ])
            })}
        </Box>
    </>;
}
